import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import {
  BODY_HEIGHT_CODING,
  BODY_TEMPERATURE_CODING,
  BODY_WEIGHT_CODING,
} from '@seen/core/models/loinc'
import { ExtendedObservation } from '@seen/core/models/observation'
import type { AnyFunction } from '@seen/core/utils'
import type { Extensions } from '@tiptap/react'
import type { DebouncedFunc } from 'lodash-es'
import { type JSX, useEffect } from 'react'
import { match } from 'ts-pattern'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'

export function ObservationNodeInlineDisplay({
  value,
  observationCode,
  observationDisplay,
  debouncedOnChange,
  additionalExtensions,
  autofocus,
  onAutofocused,
  disabled,
  noValue,
}: {
  value: string
  observationCode: string
  observationDisplay: string
  debouncedOnChange: DebouncedFunc<AnyFunction>
  additionalExtensions: Extensions
  autofocus?: boolean
  onAutofocused?: () => void
  disabled?: boolean
  // Locked before anything was entered: read as such, not as an empty field.
  noValue?: boolean
}): JSX.Element {
  useEffect(() => {
    if (autofocus) {
      onAutofocused?.()
    }
  }, [autofocus, onAutofocused])

  return (
    <div className="relative inline-flex items-baseline bg-frame align-baseline">
      <span className="font-mono">
        {ExtendedObservation.getNickname(observationCode, observationDisplay)}
        &nbsp;
      </span>
      {noValue ? (
        <span className="text-foreground-secondary">no value</span>
      ) : (
        <RichTextEditor
          variant="unstyled"
          placeholder={getPlaceholder(observationCode)}
          onChange={debouncedOnChange}
          className="text-foreground-secondary [&_.is-empty]:font-mono [&_.is-empty]:before:underline"
          value={value}
          additionalExtensions={additionalExtensions}
          onBlur={debouncedOnChange.flush}
          autofocus={autofocus ? 'end' : false}
          disabled={disabled}
        />
      )}
    </div>
  )
}

function getPlaceholder(code: string): string {
  return match<string | undefined>(code)
    .with(codeFromCodeableConcept(BODY_TEMPERATURE_CODING), () => '°F   ')
    .with(codeFromCodeableConcept(BODY_WEIGHT_CODING), () => 'lbs  ')
    .with(codeFromCodeableConcept(BODY_HEIGHT_CODING), () => 'inches')
    .otherwise(() => '     ')
}
